import React, { useState, useMemo, useEffect } from "react";
import { useAppData } from "../hooks/useAppData";
import { useCuotasLote } from "../hooks/useCuotasLote";
import { Usuario } from "../hooks/useUsuarios";
import { PinInput } from "./ui/PinInput";
import { NumericKeypad } from "./ui/NumericKeypad";
import { CuotasPagosCard } from "./ui/CuotasPagosCard";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";

// Datos mock de ejemplo
const lotesMock = [
  {
    numero: "123",
    cuotas: [
      { fecha: "2024-07-01", monto: 1000, pagada: true, vencida: false },
      { fecha: "2024-08-01", monto: 1000, pagada: false, vencida: false },
      { fecha: "2024-09-01", monto: 1000, pagada: false, vencida: false },
    ],
    totalPagado: 1000,
    saldoPendiente: 2000,
  },
  {
    numero: "124",
    cuotas: [
      { fecha: "2024-07-01", monto: 800, pagada: true, vencida: false },
      { fecha: "2024-08-01", monto: 800, pagada: true, vencida: false },
      { fecha: "2024-09-01", monto: 800, pagada: false, vencida: true },
    ],
    totalPagado: 1600,
    saldoPendiente: 800,
  },
];

type Cuota = {
  fecha: string;
  monto: number;
  pagada: boolean;
  vencida: boolean;
};

function getCuotaEstado(cuota: Cuota, idx: number, cuotas: Cuota[]) {
  if (cuota.pagada) return 'pagada';
  if (cuota.vencida) return 'vencida';
  // próxima a pagar: la primera no pagada y no vencida
  const idxProxima = cuotas.findIndex((c: Cuota) => !c.pagada && !c.vencida);
  if (idx === idxProxima) return 'proxima';
  return 'futura';
}

function getCuotaColor(estado: string) {
  switch (estado) {
    case 'pagada': return 'bg-green-100 border-green-500 text-green-700';
    case 'proxima': return 'bg-orange-100 border-orange-500 text-orange-700';
    case 'futura': return 'bg-gray-100 border-gray-300 text-gray-500';
    case 'vencida': return 'bg-red-100 border-red-500 text-red-700';
    default: return 'bg-gray-100 border-gray-300 text-gray-500';
  }
}

export default function CompradorDashboard({ user }: { user: { userType: string; userInput: string; pin: string } }) {
  const { lots, usuarios } = useAppData();
  const [loteSeleccionado, setLoteSeleccionado] = useState<string | null>(null);
  const [autenticado, setAutenticado] = useState<boolean | null>(null);
  const [intentosFallidos, setIntentosFallidos] = useState(0);
  const [pinActual, setPinActual] = useState(user.pin || "");
  const [bloqueado, setBloqueado] = useState(false);
  const [tiempoBloqueo, setTiempoBloqueo] = useState(0);
  const [mostrarTeclado, setMostrarTeclado] = useState(false);

  // Autenticación del comprador
  const usuarioAutenticado = useMemo(() => {
    if (!user.userInput || !pinActual) return null;

    // Buscar usuario por número de lote y PIN
    const lotesInput = user.userInput.split(',').map(l => l.trim());
    const usuario = usuarios.find((u: Usuario) =>
      u.tipo === 'comprador' &&
      u.pin === pinActual &&
      u.lotesAsignados?.some((lote: string) => lotesInput.includes(lote))
    );

    return usuario;
  }, [usuarios, user.userInput, pinActual]);

  // Filtrar lotes del comprador autenticado (solo lotes a crédito)
  const lotesComprador = useMemo(() => {
    if (!usuarioAutenticado) return [];

    const lotesUsuario = usuarioAutenticado.lotesAsignados || [];
    return lots.filter((lote: any) => {
      // Solo lotes a crédito
      if (lote.status !== 'crédito' && lote.paymentType !== 'pagos') return false;

      // Buscar por ID individual o en grupos
      if (lotesUsuario.includes(lote.id)) return true;
      if (Array.isArray(lote.idLotes) && lote.idLotes.some((id: string) => lotesUsuario.includes(id))) return true;
      return false;
    });
  }, [lots, usuarioAutenticado]);

  // Usar el hook para obtener datos de cuotas y pagos
  const { cuotasData, loading, error } = useCuotasLote(lotesComprador);

  // Verificar autenticación y lotes a crédito
  useEffect(() => {
    if (usuarioAutenticado) {
      // Verificar que tenga al menos un lote a crédito
      const tieneLotesCredito = lotesComprador.length > 0;
      if (tieneLotesCredito) {
        setAutenticado(true);
        setIntentosFallidos(0);
        setMostrarTeclado(false);
      } else {
        setAutenticado(false);
        setMostrarTeclado(true);
      }
    } else if (usuarios.length > 0 && pinActual.length === 4) { // PIN completo pero autenticación fallida
      setAutenticado(false);
      setIntentosFallidos(prev => prev + 1);
      setMostrarTeclado(true);
      setPinActual("");

      // Bloquear después de 5 intentos
      if (intentosFallidos >= 4) { // 4 porque ya se incrementó arriba
        setBloqueado(true);
        setTiempoBloqueo(30); // 30 segundos de bloqueo
      }
    } else if (usuarios.length > 0 && !pinActual) { // No hay PIN, mostrar teclado
      setMostrarTeclado(true);
    }
  }, [usuarioAutenticado, usuarios.length, lotesComprador.length, pinActual, intentosFallidos]);

  // Manejar bloqueo temporal
  useEffect(() => {
    if (bloqueado && tiempoBloqueo > 0) {
      const timer = setTimeout(() => {
        setTiempoBloqueo(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (bloqueado && tiempoBloqueo === 0) {
      setBloqueado(false);
      setIntentosFallidos(0);
      setPinActual("");
    }
  }, [bloqueado, tiempoBloqueo]);

  // Manejar PIN completo
  const handlePinComplete = (val: string) => {
    if (val.length === 4) {
      setPinActual(val);
    }
  };

  // Si está bloqueado, mostrar pantalla de bloqueo
  if (bloqueado) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Acceso Bloqueado</h2>
          <p className="text-gray-600 mb-4">
            Demasiados intentos fallidos. Intenta de nuevo en:
          </p>
          <div className="text-3xl font-bold text-blue-600 mb-4">
            {Math.floor(tiempoBloqueo / 60)}:{(tiempoBloqueo % 60).toString().padStart(2, '0')}
          </div>
          <p className="text-sm text-gray-500">
            Por seguridad, tu cuenta ha sido bloqueada temporalmente.
          </p>
        </div>
      </div>
    );
  }

  // Si no está autenticado y debe mostrar teclado, mostrar pantalla de login con teclado
  if (autenticado === false && mostrarTeclado) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex flex-col items-center px-4 pt-20">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Bimobi</h1>
            <h2 className="text-xl font-semibold text-center mb-1">Acceso de Comprador</h2>
            <p className="text-gray-500 text-center">Propietario de lotes</p>
          </div>

          {/* Mostrar número de lote */}
          <div className="w-full max-w-md mb-6">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Número de Lote
            </label>
            <div className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-gray-100 text-gray-700">
              {user.userInput}
            </div>
          </div>

          {/* Mensaje de error */}
          <div className="w-full max-w-md mb-6">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-red-600 mb-2">Acceso Denegado</h2>
              <p className="text-gray-600 mb-2">
                El número de lote o PIN ingresado no es válido.
              </p>
              <p className="text-sm text-gray-500">
                Intentos restantes: {5 - intentosFallidos}
              </p>
            </div>
          </div>

          {/* Teclado PIN */}
          <div className="w-full max-w-md bg-gray-50 rounded-xl py-8 mb-4 flex flex-col items-center">
            <span className="mb-2 text-lg font-medium text-gray-500">Ingresa tu PIN</span>
            <PinInput value={pinActual} onChange={setPinActual} length={4} />
          </div>
          <NumericKeypad value={pinActual} length={4} onChange={setPinActual} />
          <button
            className={`w-full max-w-md mt-8 py-3 rounded-lg font-semibold text-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300
              ${pinActual.length === 4 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
            disabled={pinActual.length !== 4}
            onClick={() => handlePinComplete(pinActual)}
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  // Si aún está cargando
  if (autenticado === null || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <p className="text-gray-600">Verificando credenciales...</p>
        </div>
      </div>
    );
  }

  // Si hay error en la carga de datos
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600">Error al cargar los datos de cuotas.</p>
        </div>
      </div>
    );
  }

  const handleCardClick = (numero: string) => {
    setLoteSeleccionado(numero === loteSeleccionado ? null : numero);
  };

  function getCuotaEstado(cuota: any, idx: number, cuotas: any[]) {
    if (cuota.pagada) return 'pagada';
    if (cuota.vencida) return 'vencida';
    // próxima a pagar: la primera no pagada y no vencida
    const idxProxima = cuotas.findIndex((c: any) => !c.pagada && !c.vencida);
    if (idx === idxProxima) return 'proxima';
    return 'futura';
  }

  function getCuotaColor(estado: string) {
    switch (estado) {
      case 'pagada': return 'bg-green-100 border-green-500 text-green-700';
      case 'proxima': return 'bg-orange-100 border-orange-500 text-orange-700';
      case 'futura': return 'bg-gray-100 border-gray-300 text-gray-500';
      case 'vencida': return 'bg-red-100 border-red-500 text-red-700';
      default: return 'bg-gray-100 border-gray-300 text-gray-500';
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-6">
      <div className="w-full max-w-md flex justify-between items-center mb-8">
        <h2 className="text-xl font-semibold text-left">Mis Lotes a Crédito</h2>
        <div className="flex gap-2">
          {loteSeleccionado && (
            <button
              className="px-3 py-1 rounded-md bg-gray-200 hover:bg-blue-500 hover:text-white text-gray-700 text-xs font-semibold transition-colors shadow-sm"
              onClick={() => setLoteSeleccionado(null)}
              title="Volver"
            >
              Volver
            </button>
          )}
          <button
            className="px-3 py-1 rounded-md bg-gray-200 hover:bg-red-500 hover:text-white text-gray-700 text-xs font-semibold transition-colors shadow-sm"
            onClick={() => {
              localStorage.removeItem('bimobiUser');
              window.location.reload();
            }}
            title="Cerrar sesión"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
      <div className="w-full max-w-md space-y-4">
        {cuotasData.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            No tienes lotes a crédito pendientes de pago.<br />
            Si crees que esto es un error, contacta a soporte.
          </div>
        ) : loteSeleccionado === null ? (
          cuotasData.map((loteData: any) => (
            <Card
              key={loteData.numero}
              className="bg-white border border-gray-200 border-l-4 border-l-blue-500 rounded-xl cursor-pointer hover:shadow-lg transition"
              onClick={() => handleCardClick(loteData.numero)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 pr-4">
                <CardTitle className="text-lg font-semibold text-gray-800">Ver Cuotas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">Lote {loteData.numero}</div>
                <p className="text-xs text-gray-500 mt-1">Haz clic para ver detalles de pagos</p>
              </CardContent>
            </Card>
          ))
        ) : (
          cuotasData.filter((loteData: any) => loteData.numero === loteSeleccionado).map((loteData: any) => (
            <CuotasPagosCard key={loteData.numero} loteData={loteData} onBack={() => setLoteSeleccionado(null)} />
          ))
        )}
      </div>
    </div>
  );
} 